import { useState, useEffect } from 'react';

export interface EditableImage {
  url: string;
  alt: string;
}

export interface EditableMethod {
  icon: string;
  title: string;
  description: string;
}

export interface EditableTarif {
  title: string;
  subtitle: string;
  price: string;
  priceSubtitle: string;
  features: string[];
  isPopular?: boolean;
  buttonText: string;
}

export interface EditableFAQ {
  question: string;
  answer: string;
}

export interface EditableContent {
  pageTitle: string;
  pageSubtitle: string;
  heroImage: EditableImage;
  
  // Section Description
  descriptionTitle: string;
  descriptionText1: string;
  descriptionText2: string;
  descriptionImage: EditableImage;
  descriptionFeatures: EditableMethod[];
  
  // Section Méthodes
  methodsTitle: string;
  methodsSubtitle: string;
  methods: EditableMethod[];
  methodsInfo: {
    title: string;
    content: string;
  };
  
  // Section Tarifs
  tarifsTitle: string;
  tarifsSubtitle: string;
  tarifs: EditableTarif[];
  
  // Section FAQ
  faqTitle: string;
  faqSubtitle: string;
  faqs: EditableFAQ[];
  faqContactText: string;
  faqContactSubtext: string;
  
  // Section Formulaire
  formTitle: string;
  formSubtitle: string;
}

const STORAGE_PREFIX = 'r3d_editable_content_';

export function useEditableContent(pageKey: string, defaultContent: EditableContent) {
  const [content, setContent] = useState<EditableContent>(defaultContent);
  const [isLoading, setIsLoading] = useState(true);

  // Charger le contenu depuis localStorage au montage
  useEffect(() => {
    const storageKey = `${STORAGE_PREFIX}${pageKey}`;
    const savedContent = localStorage.getItem(storageKey);
    
    if (savedContent) {
      try {
        const parsed = JSON.parse(savedContent);
        setContent(parsed);
      } catch (error) {
        console.error('Erreur lors du chargement du contenu:', error);
        setContent(defaultContent);
      }
    } else {
      setContent(defaultContent);
    }
    
    setIsLoading(false);
  }, [pageKey]);

  // Sauvegarder le contenu dans localStorage
  const saveContent = (newContent: EditableContent) => {
    const storageKey = `${STORAGE_PREFIX}${pageKey}`;
    try {
      localStorage.setItem(storageKey, JSON.stringify(newContent));
      setContent(newContent);
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du contenu:', error);
      return false;
    }
  };

  // Réinitialiser aux valeurs par défaut
  const resetToDefault = () => {
    const storageKey = `${STORAGE_PREFIX}${pageKey}`;
    localStorage.removeItem(storageKey);
    setContent(defaultContent);
  };

  // Mettre à jour une partie du contenu
  const updateContent = (updates: Partial<EditableContent>) => {
    const newContent = { ...content, ...updates };
    return saveContent(newContent);
  };

  return {
    content,
    isLoading,
    saveContent,
    updateContent,
    resetToDefault
  };
}